import React, { useEffect, useMemo, useRef, useState } from "react";
import Papa from "papaparse";

/* =========================
   Konstante in helperji
   ========================= */
const STORE_LIST = ["SPAR", "Mercator", "Hofer"];

// Kandidati za CSV glede na možno pot (/, /optinakup/)
const CSV_CANDIDATES = (() => {
  const url = new URL(window.location.href);
  const a = new URL("catalog_merged.csv", url).toString();
  const b = `${url.origin}/catalog_merged.csv`;
  const c = `${url.origin}/optinakup/catalog_merged.csv`;
  return [...new Set([a, b, c])];
})();

const EU = new Intl.NumberFormat("sl-SI", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

function toNum(v) {
  if (v == null) return NaN;
  if (typeof v === "number") return v > 100 ? +(v / 100).toFixed(2) : +v;
  const s = String(v)
    .replace(/\u00A0/g, " ")
    .replace(/\./g, "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "")
    .trim();
  const m = s.match(/-?\d+(?:\.\d+)?/);
  const n = m ? parseFloat(m[0]) : NaN;
  return isNaN(n) ? NaN : +n.toFixed(2);
}
function clampText(s = "", max = 160) {
  const t = (s || "").toString().trim();
  return t.length > max ? t.slice(0, max - 1) + "\u2026" : t;
}
// Iz besedila izlušči gramaturo/volumen (npr. "500 g", "330ml", "1 l", "2 kg")
function extractSize(s) {
  if (!s) return "";
  const t = String(s);
  // ujame: "500 g", "0,5 l", "1l", "330ml", "2 kg", "750 ml", ...
  const m = t.match(/(\d+(?:[.,]\d+)?)\s?(kg|g|l|ml)\b/i);
  if (!m) return "";
  const val = m[1].replace(",", ".");
  const unit = m[2].toLowerCase();
  return `${val} ${unit}`;
}

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}
const pick = (r, keys, fallback = "") => {
  for (const k of keys) if (r[k] != null && r[k] !== "") return r[k];
  return fallback;
};

/* =========================
   Global force stili (barve)
   ========================= */
const ForceStyles = () => (
  <style>{`
    .force-blue { color:#0284c7 !important; }
    button.force-btn, .force-btn {
      background:#0284c7 !important; border-color:#0284c7 !important; color:#ffffff !important;
    }
    button.force-btn--green, .force-btn--green {
      background:#16a34a !important; border-color:#16a34a !important; color:#ffffff !important;
    }
    .btn-reset { appearance:none !important; -webkit-appearance:none !important; }
  `}</style>
);

/* =========================
   Minimalen UI
   ========================= */
const Card = ({ className = "", children }) => (
  <div className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>{children}</div>
);
const CardContent = ({ className = "", children }) => <div className={`p-4 ${className}`}>{children}</div>;
const Button = ({ className = "", children, ...rest }) => (
  <button {...rest} className={`px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-40 border ${className}`}>
    {children}
  </button>
);

/* =========================
   Header (filtri, iskanje, gumb seznama desno)
   ========================= */
function Header({
  selectedStores,
  toggleStore,
  availableCount,
  searchText,
  setSearchText,
  searchRef,
  basketCount,
  onOpenBasket,
}) {
  return (
    <div className="w-full max-w-5xl mx-auto p-4 relative">
      {/* Gumb Nakupovalni seznam — absolutno desno zgoraj */}
      <button
        onClick={onOpenBasket}
        type="button"
        className="absolute right-4 top-4 relative rounded-2xl border px-4 py-3 text-base hover:opacity-95"
        title="Odpri nakupovalni seznam"
        aria-label="Nakupovalni seznam"
        style={{ backgroundColor: "#f97316", borderColor: "#f97316", color: "#ffffff" }} // orange-500
      >
        {/* Ikona seznama */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <line x1="8" y1="8" x2="16" y2="8" stroke="currentColor" strokeWidth="1.6" />
          <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="1.6" />
          <line x1="8" y1="16" x2="16" y2="16" stroke="currentColor" strokeWidth="1.6" />
        </svg>
        {/* Značka (tudi 0) */}
        <span
          className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 rounded-full text-white text-xs grid place-items-center"
          style={{ backgroundColor: "#0284c7" }} // orange-900
        >
          {basketCount}
        </span>
      </button>

      <h1 className="text-3xl font-bold text-center mb-4 force-blue">Optimalni nakup</h1>

      <div className="flex flex-wrap justify-center gap-2 mb-3">
        {STORE_LIST.map((s) => (
          <button
            key={s}
            onClick={() => toggleStore(s)}
            className={`px-3 py-1 rounded-xl border ${
              selectedStores.has(s) ? "bg-white" : "bg-gray-100 opacity-60"
            }`}
            title={`Prikaži/skrij ${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Iskalnik */}
      <div className="mb-2">
        <input
          ref={searchRef}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          className="w-full h-12 px-4 rounded-2xl border-2 border-sky-400 outline-none focus:ring-4 focus:ring-sky-200 shadow-sm text-center"
          placeholder="Iskalni niz – min. 5 znakov"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      {/* Števec pod iskalnikom */}
      <div className="text-sm text-gray-600 text-center mb-2">
        Izdelkov v bazi: <b>{availableCount}</b>
      </div>
    </div>
  );
}

/* =========================
   Stran: Nakupovalni seznam (nova “page”)
   ========================= */
function BasketPage({ items, total, onRemove, onBack, onExportPdf }) {
  // sortiraj po trgovini (A–Ž)
  const itemsSorted = useMemo(() => {
    return [...(items || [])].sort((a, b) =>
      String(a?.r?.store || "").localeCompare(String(b?.r?.store || ""), "sl", { sensitivity: "base" })
    );
  }, [items]);

  return (
    <main className="w-full max-w-5xl mx-auto p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-lg">Nakupovalni seznam</div>
        <div className="flex items-center gap-2">
          <Button className="force-btn" onClick={onExportPdf}>PDF</Button>
          <Button className="force-btn" onClick={onBack}>Nazaj</Button>
        </div>
      </div>

      {(!itemsSorted || itemsSorted.length === 0) ? (
        <div className="text-sm text-gray-500">Seznam je prazen.</div>
      ) : (
        <Card>
          <CardContent className="p-3">
            {/* Header vrstica – zelo kompaktna */}
            <div className="grid grid-cols-[90px,1fr,100px,72px,90px,32px] gap-2 items-center pb-1 border-b text-[11px] uppercase tracking-wide text-gray-500">
              <div>Trgovina</div>
              <div>Artikel</div>
              <div className="text-right">Gramatura</div>
              <div className="text-right">Količina</div>
              <div className="text-right">Cena</div>
              <div></div>
            </div>

            {/* Vrstice – kompaktno (nižji padding, manjši font) */}
            <div className="text-[13px]">
              {itemsSorted.map((it) => {
                const name = it?.r?.name || "";
                const store = it?.r?.store || "";
                const price = isNaN(it.price) ? NaN : it.price;
                const size = extractSize(name) || extractSize(it?.r?.desc || "");

                return (
                  <div
                    key={it.id}
                    className="grid grid-cols-[90px,1fr,100px,72px,90px,32px] gap-2 items-center border-b py-1"
                  >
                    {/* Trgovina */}
                    <div className="truncate">{store}</div>

                    {/* Artikel */}
                    <div className="min-w-0 truncate" title={name}>
                      {clampText(name, 160)}
                    </div>

                    {/* Gramatura/ml */}
                    <div className="text-right">{size || "—"}</div>

                    {/* Količina */}
                    <div className="text-right">{it.qty}</div>

                    {/* Cena (na kos) */}
                    <div className="text-right">{isNaN(price) ? "—" : EU.format(price)}</div>

                    {/* Brisanje – koš (rdeč) */}
                    <button
                      className="p-1"
                      title="Odstrani"
                      aria-label="Odstrani s seznama"
                      onClick={() => onRemove(it.id)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        {/* pokrov */}
                        <path d="M4 7h16" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round"/>
                        <path d="M9 4h6" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round"/>
                        {/* koš */}
                        <rect x="6.5" y="7" width="11" height="12.5" rx="1.8" stroke="#dc2626" strokeWidth="1.8"/>
                        {/* notranje črte */}
                        <path d="M10 10v7M14 10v7" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* skupaj (po želji pustiš, čeprav ga nisi zahteval izpisovati v stolpcu) */}
            <div className="text-right font-semibold text-[13px] pt-2">
              SKUPAJ: {EU.format(total)}
            </div>
          </CardContent>
        </Card>
      )}
    </main>
  );
}


/* =========================
   Kartica rezultata
   ========================= */
const ResultCard = React.memo(function ResultCard({ r, qty, onInc, onDec, onAdd, isAdded }) {
  const price = toNum(r.price_eur);
  const uprice = toNum(r.unit_price_eur);
  const hasUrl = r.product_url && String(r.product_url).startsWith("http");

  const sourceLabel = (() => {
    const url = String(r.product_url || "");
    const img = String(r.image_url || "");
    const isLeaflet = /letak|leaflet|katalog|bro[sš]ur|broch/i.test(url) || /page|letak/i.test(img);
    if (isLeaflet) return "Letak";
    if (url) return "Spletna cena";
    return "";
  })();

  return (
    <Card className="mb-2">
      <CardContent className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-y-2 gap-x-3 items-center">
          {/* Levo */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-16 h-16 bg-gray-100 flex items-center justify-center rounded">
              {r.image_url ? (
                <img
                  src={r.image_url}
                  alt={r.name || ""}
                  className="w-full h-full object-contain rounded"
                  loading="lazy"
                />
              ) : (
                <div className="text-xs text-gray-400">Ni slike</div>
              )}
            </div>
            <div className="min-w-0">
              <div className="font-medium truncate" title={r.name || ""}>
                {hasUrl ? (
                  <a
                    href={r.product_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    {clampText(r.name || "", 120)}
                  </a>
                ) : (
                  clampText(r.name || "", 120)
                )}
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <span>{r.store || ""}</span>
                {sourceLabel && (
                  <span className="px-2 py-0.5 rounded-full border text-[10px] leading-none">
                    {sourceLabel}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Desno */}
          <div className="flex items-center justify-between md:justify-end gap-3 w-full md:w-auto">
            <div className="text-right">
              <div className="font-semibold">{isNaN(price) ? "-" : EU.format(price)}</div>
              <div className="text-xs text-gray-500">
                {isNaN(uprice) ? "" : `${EU.format(uprice)} / enoto`}
              </div>
            </div>

            {/* Gumbi */}
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 rounded border"
                onClick={onDec}
                disabled={(qty || 0) <= 0}
              >
                −
              </button>
              <div className="w-10 h-8 grid place-items-center rounded border select-none">
                {qty || 0}
              </div>
              <button className="px-3 py-1 rounded border" onClick={onInc}>
                +
              </button>

              <button
                type="button"
                className={`px-3 py-2 rounded-lg text-sm font-semibold border disabled:opacity-50 disabled:cursor-not-allowed btn-reset ${
                  isAdded ? "force-btn--green" : "force-btn"
                }`}
                onClick={onAdd}
                disabled={(qty || 0) <= 0 || isNaN(price)}
                style={
                  isAdded
                    ? {
                        backgroundColor: "#16a34a",
                        borderColor: "#16a34a",
                        color: "#ffffff",
                      }
                    : {
                        backgroundColor: "#0284c7",
                        borderColor: "#0284c7",
                        color: "#ffffff",
                      }
                }
              >
                {isAdded ? "Dodano" : "Dodaj"}
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

/* =========================
   Aplikacija
   ========================= */
export default function App() {
  // Pogled: 'search' | 'basket'
  const [view, setView] = useState("search");

  // Podatki
  const [data, setData] = useState([]);
  const [selectedStores, setSelectedStores] = useState(new Set(STORE_LIST));

  // Iskanje
  const [searchText, setSearchText] = useState("");
  const debounced = useDebounced(searchText, 300);
  const [query, setQuery] = useState("");
  useEffect(() => setQuery(debounced), [debounced]);

  // Paginacija (10/stran)
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [query]);

  // Ref za iskalnik
  const searchRef = useRef(null);

  // Količine + košarica + indikator “Dodano”
  const [quantities, setQuantities] = useState({}); // id -> qty
  const [basket, setBasket] = useState({}); // id -> qty
  const [added, setAdded] = useState(new Set()); // pravkar dodani id-ji

  const markAdded = (id) => {
    setAdded((prev) => {
      const ns = new Set(prev);
      ns.add(id);
      return ns;
    });
    setTimeout(() => {
      setAdded((prev) => {
        const ns = new Set(prev);
        ns.delete(id);
        return ns;
      });
    }, 1200);
  };

  const inc = (id) => setQuantities((q) => ({ ...q, [id]: (q[id] || 0) + 1 }));
  const dec = (id) =>
    setQuantities((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) - 1) }));
  const addToList = (id) =>
    setBasket((b) => {
      const addQty = quantities[id] || 0;
      if (addQty <= 0) return b;
      const nb = { ...b, [id]: (b[id] || 0) + addQty };
      setQuantities((q) => ({ ...q, [id]: 0 }));
      markAdded(id);
      return nb;
    });
  const removeFromList = (id) =>
    setBasket((b) => {
      const nb = { ...b };
      delete nb[id];
      return nb;
    });

  // CSV nalaganje
  useEffect(() => {
    const parseCsvText = (text) => {
      let res = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
      });
      if ((!res.data || res.data.length === 0) && text.includes(";")) {
        res = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h) => h.trim(),
          delimiter: ";",
        });
      }
      return res;
    };
    const tryFetch = async (u) => {
      const bust = u + (u.includes("?") ? "&" : "?") + "v=" + Date.now();
      const r = await fetch(bust, { cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status} ${r.statusText} @ ${u}`);
      const text = await r.text();
      return { u, text };
    };
    (async () => {
      let lastErr = null;
      for (const u of CSV_CANDIDATES) {
        try {
          const { text } = await tryFetch(u);
          const parsed = parseCsvText(text);
          const rows = (parsed.data || []).map((r, idx) => ({
            id: idx + 1,
            store: pick(r, ["store", "Store", "trgovec"], ""),
            name: pick(r, ["name", "naziv", "title"], ""),
            desc: pick(r, ["desc", "opis", "description"], ""),
            image_url: pick(r, ["image_url", "img", "image", "slika"], ""),
            product_url: pick(r, ["product_url", "url", "link"], ""),
            price_eur: toNum(pick(r, ["price_eur", "price", "cena_eur", "cena"], "")),
            unit_price_eur: toNum(
              pick(r, ["unit_price_eur", "unit_price", "cena_na_enoto", "cena_na_kg_l"], "")
            ),
          }));
          if (!rows.length) throw new Error("Parser vrnil 0 vrstic @ " + u);
          setData(rows);
          setSelectedStores(new Set(["SPAR", "Mercator", "Hofer"]));
          return;
        } catch (e) {
          lastErr = e;
        }
      }
      alert("Napaka pri branju CSV.\nZadnji poskus: " + (lastErr?.message || String(lastErr)));
    })();
  }, []);

  // Izračuni
  const availableCount = useMemo(
    () => data.filter((r) => selectedStores.has(r.store)).length,
    [data, selectedStores]
  );

  const byId = useMemo(() => {
    const m = new Map();
    for (const r of data) m.set(r.id, r);
    return m;
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 5) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    return data
      .filter((r) => {
        if (!selectedStores.has(r.store)) return false;
        const bucket = `${r.name || ""} ${r.store || ""} ${r.desc || ""}`.toLowerCase();
        return tokens.every((t) => bucket.includes(t));
      })
      .map((r) => ({ ...r, __price: toNum(r.price_eur), __uprice: toNum(r.unit_price_eur) }))
      .sort((a, b) => {
        const ua = isNaN(a.__uprice) || a.__uprice <= 0 ? Infinity : a.__uprice;
        const ub = isNaN(b.__uprice) || b.__uprice <= 0 ? Infinity : b.__uprice;
        if (ua !== ub) return ua - ub;
        const pa = isNaN(a.__price) ? Infinity : a.__price;
        const pb = isNaN(b.__price) ? Infinity : b.__price;
        return pa - pb;
      });
  }, [data, selectedStores, query]);

  // Paginacija (ena sama različica!)
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const startIdx = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const basketItems = useMemo(
    () =>
      Object.entries(basket)
        .map(([id, qty]) => {
          const r = byId.get(+id);
          if (!r) return null;
          const price = toNum(r.price_eur);
          return { id: +id, r, qty, price };
        })
        .filter(Boolean),
    [basket, byId]
  );
  const total = useMemo(
    () => basketItems.reduce((sum, it) => sum + (isNaN(it.price) ? 0 : it.price) * it.qty, 0),
    [basketItems]
  );
  const basketCount = useMemo(() => Object.values(basket).reduce((s, n) => s + n, 0), [basket]);

  // PDF izvoz
  const exportBasketToPDF = () => {
    const win = window.open("", "PRINT", "height=900,width=700");
    if (!win) return;
    const rowsHtml = basketItems
      .map(
        (it) => `
          <tr>
            <td style="padding:6px 8px;border-bottom:1px solid #eee">${(it.r.name || "").replace(/</g,"&lt;")}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${it.qty}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${isNaN(it.price)?"—":EU.format(it.price)}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${EU.format((isNaN(it.price)?0:it.price)*it.qty)}</td>
          </tr>`
      )
      .join("");
    win.document.write(`
      <html><head><meta charset="utf-8"/>
        <title>Nakupovalni seznam</title>
        <style>
          body{font:14px system-ui,-apple-system,Segoe UI,Roboto,Arial;padding:20px}
          h1{font-size:18px;margin:0 0 12px}
          table{width:100%;border-collapse:collapse}
          th{text-align:left;border-bottom:2px solid #000;padding:6px 8px}
          tfoot td{font-weight:700;border-top:2px solid #000;padding:8px}
        </style>
      </head><body>
        <h1>Nakupovalni seznam – ${new Date().toLocaleString("sl-SI")}</h1>
        <table>
          <thead><tr>
            <th>Artikel</th>
            <th style="text-align:right">Količina</th>
            <th style="text-align:right">Cena</th>
            <th style="text-align:right">Skupaj</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
          <tfoot><tr>
            <td colspan="3" style="text-align:right">SKUPAJ:</td>
            <td style="text-align:right">${EU.format(total)}</td>
          </tr></tfoot>
        </table>
        <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),100);}</script>
      </body></html>
    `);
    win.document.close();
  };

  const toggleStore = (store) => {
    const ns = new Set(selectedStores);
    ns.has(store) ? ns.delete(store) : ns.add(store);
    setSelectedStores(ns);
  };

  return (
    <div className="min-h-screen bg-white">
      <ForceStyles />

      {/* Header z gumbom za seznam v desnem zgornjem kotu */}
      <Header
        selectedStores={selectedStores}
        toggleStore={toggleStore}
        availableCount={availableCount}
        searchText={searchText}
        setSearchText={(v) => {
          setSearchText(v);
          if (view !== "search") setView("search");
        }}
        searchRef={searchRef}
        basketCount={basketCount}
        onOpenBasket={() => setView("basket")}
      />

      {/* Pogledi */}
      {view === "basket" ? (
        <BasketPage
          items={basketItems}
          total={total}
          onRemove={removeFromList}
          onBack={() => setView("search")}
          onExportPdf={exportBasketToPDF}
        />
      ) : (
        <main className="w-full max-w-5xl mx-auto p-4">
          {query.trim().length < 5 && (
            <div className="text-center text-gray-500 mb-4">Vnesi vsaj 5 znakov za iskanje.</div>
          )}

          {pageItems.map((r) => (
            <ResultCard
              key={`${r.id}-${r.store}-${r.name}`}
              r={r}
              qty={quantities[r.id] || 0}
              onInc={() => inc(r.id)}
              onDec={() => dec(r.id)}
              onAdd={() => addToList(r.id)}
              isAdded={added.has(r.id)}
            />
          ))}

          {/* Paginacija (10/stran) */}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 select-none">
              <Button disabled={page === 1} onClick={() => setPage(1)}>
                &laquo; Prva
              </Button>
              <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
                &lsaquo; Nazaj
              </Button>
              <span className="text-sm">
                Stran {page} / {totalPages} ({filtered.length} zadetkov)
              </span>
              <Button disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                Naprej &rsaquo;
              </Button>
              <Button disabled={page === totalPages} onClick={() => setPage(totalPages)}>
                Zadnja &raquo;
              </Button>
            </div>
          )}
        </main>
      )}
    </div>
  );
}
